import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { Modal, Notice, Pager, Stat } from "../../lib/ui";

/*
 * Client accounts & user permissions – port of user-management.php (API: backend/users.py)
 * Create a login for a client, change a user's role, deactivate / reactivate an account.
 * The table supports search, sorting and paging (the PHP page used DataTables for this).
 */

const PAGE_SIZES = [10, 25, 50, 100];
const STATUS = {
  none: { label: "No account", cls: "" },
  active: { label: "Active", cls: "ok" },
  inactive: { label: "Deactivated", cls: "err" },
};

export default function UserManagement() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [notice, setNotice] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ col: "client_id", dir: 1 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [roleModal, setRoleModal] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [statusModal, setStatusModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get("/api/users")); }
    catch (e) { setNotice({ tone: "err", text: e.message }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const act = async (body) => {
    setBusy(true);
    try {
      const r = await api.post("/api/users", body);
      setNotice({ tone: r.tone, text: r.message });
      load();
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
    finally { setBusy(false); setRoleModal(null); setStatusModal(null); }
  };

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = (data?.rows || []).filter((r) => !term || [r.client_id, r.client_name, r.email, r.phone, r.role, STATUS[r.status].label]
      .some((v) => String(v ?? "").toLowerCase().includes(term)));
    return [...list].sort((a, b) => {
      const x = a[sort.col] ?? "", y = b[sort.col] ?? "";
      return (typeof x === "number" ? x - y : String(x).localeCompare(String(y))) * sort.dir;
    });
  }, [data, q, sort]);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const shown = rows.slice((Math.min(page, totalPages) - 1) * pageSize, Math.min(page, totalPages) * pageSize);

  const Th = ({ col, children }) => (
    <th className="sortable" onClick={() => { setSort((s) => ({ col, dir: s.col === col ? -s.dir : 1 })); setPage(1); }}
      aria-sort={sort.col === col ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
      {children} {sort.col === col && <i className={`fas ${sort.dir === 1 ? "fa-sort-up" : "fa-sort-down"}`} />}
    </th>
  );

  return (
    <main className="pg">
      <PageHeader slug="user-management" />
      <Notice notice={notice} onClose={() => setNotice(null)} />

      {data && (
        <div className="ui-stats">
          <Stat icon="fa-users" label="Clients" value={data.totals.clients} />
          <Stat icon="fa-user-check" tone="up" label="Active users" value={data.totals.active} />
          <Stat icon="fa-user-slash" tone="danger" label="Deactivated" value={data.totals.inactive} />
          <Stat icon="fa-user-plus" tone="muted" label="No account" value={data.totals.no_account} />
        </div>
      )}

      <section className="panel">
        <div className="toolbar">
          <div className="field grow"><div className="search"><i className="fas fa-magnifying-glass" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search clients" aria-label="Search clients" /></div></div>
          <div className="field" style={{ minWidth: "7rem" }}><label htmlFor="um-size">Show</label>
            <select id="um-size" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} rows</option>)}
            </select></div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr>
              <Th col="client_id">ID</Th><Th col="client_name">Name</Th><Th col="email">Email</Th><Th col="phone">Phone</Th>
              <Th col="role">Role</Th><Th col="status">Status</Th><th>Actions</th>
            </tr></thead>
            <tbody>
              {!data && [0, 1, 2].map((i) => <tr key={i}>{[0, 1, 2, 3, 4, 5, 6].map((c) => <td key={c}><div className="skel" /></td>)}</tr>)}
              {shown.map((r) => (
                <tr key={r.client_id}>
                  <td className="mono">{r.client_id}</td>
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", fontWeight: 600, color: "var(--ink)" }}>
                      <span className="avatar">{(r.client_name || "?").charAt(0).toUpperCase()}</span>
                      <Link to={`/update_client/${r.client_id}`} style={{ color: "inherit" }}>{r.client_name}</Link>
                    </span>
                    {r.username && <div style={{ fontSize: ".75rem", color: "var(--muted)", marginLeft: "2.3rem" }}>@{r.username}</div>}
                  </td>
                  <td>{r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : <span style={{ color: "#c7c4bc" }}>–</span>}</td>
                  <td>{r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : <span style={{ color: "#c7c4bc" }}>–</span>}</td>
                  <td>{r.role || <span style={{ color: "#c7c4bc" }}>–</span>}</td>
                  <td><span className={`pill-tag ${STATUS[r.status].cls}`}>{STATUS[r.status].label}</span></td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {r.status === "none" ? (
                      <button type="button" className="btn sm primary" disabled={busy} onClick={() => act({ action: "make_user", client_id: r.client_id })}>
                        <i className="fas fa-user-plus" /> Create user</button>
                    ) : (
                      <span style={{ display: "inline-flex", gap: ".35rem" }}>
                        <button type="button" className="btn sm" onClick={() => { setNewRole(""); setRoleModal(r); }}><i className="fas fa-shield-halved" /> Role</button>
                        <button type="button" className={`btn sm ${r.status === "active" ? "" : "primary"}`} onClick={() => setStatusModal(r)}>
                          {r.status === "active" ? "Deactivate" : "Reactivate"}</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && rows.length === 0 && <div className="empty"><i className="fas fa-users" />No clients found.</div>}
        </div>
        {data && (
          <div className="ui-pager" style={{ justifyContent: "space-between" }}>
            <span>Showing {rows.length ? (Math.min(page, totalPages) - 1) * pageSize + 1 : 0} to {Math.min(Math.min(page, totalPages) * pageSize, rows.length)} of {rows.length} clients
              {rows.length !== data.rows.length ? ` (filtered from ${data.rows.length})` : ""}</span>
            <Pager page={Math.min(page, totalPages)} totalPages={totalPages} onPage={setPage} />
          </div>
        )}
      </section>

      {roleModal && (
        <Modal title="Change user role" onClose={() => setRoleModal(null)}
          actions={<><button type="button" className="btn" onClick={() => setRoleModal(null)}>Cancel</button>
            <button type="button" className="btn primary" disabled={!newRole || busy}
              onClick={() => act({ action: "change_role", client_id: roleModal.client_id, new_role: Number(newRole) })}>Update role</button></>}>
          <p style={{ marginTop: 0 }}>Updating permissions for <b>{roleModal.client_name}</b> (currently {roleModal.role}).</p>
          <div className="field"><label htmlFor="um-role">New role</label>
            <select id="um-role" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
              <option value="" disabled>Select a role</option>
              {data.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select></div>
        </Modal>
      )}
      {statusModal && (
        <Modal title={statusModal.status === "active" ? "Deactivate account?" : "Reactivate account?"} onClose={() => setStatusModal(null)}
          actions={<><button type="button" className="btn" onClick={() => setStatusModal(null)}>Cancel</button>
            <button type="button" className={`btn ${statusModal.status === "active" ? "danger" : "primary"}`} disabled={busy}
              onClick={() => act({ action: "toggle_active", client_id: statusModal.client_id, new_status: statusModal.status === "active" ? 0 : 1 })}>
              {statusModal.status === "active" ? "Deactivate" : "Reactivate"}</button></>}>
          <b>{statusModal.client_name}</b>{" "}
          {statusModal.status === "active" ? "will immediately lose the ability to log in until reactivated." : "will be able to log in again."}
        </Modal>
      )}
    </main>
  );
}
